import { Link } from "react-router-dom";

import { PageHeader } from "@/customer/shell/PageHeader";

import { useDashboard, useReadyForPickup } from "./api";
import { daysUntil, formatDate } from "./format";
import type { DashboardOrderCard, DashboardPayload } from "./types";
import { StatusPill } from "./components/StatusPill";
import { CountdownChip } from "./components/CountdownChip";
import { EquipmentThumb } from "./components/EquipmentThumb";
import { LifecycleStepper } from "./components/LifecycleStepper";
import { QuickAccessRail } from "./components/QuickAccessRail";

// Phase 4a customer-portal FR-3 + Phase 4j portal-polish — dashboard
// with three sections (active / upcoming / past), a 3-tile KPI strip
// up top, and equipment thumbnails on each card. Audit grade lift:
// B- → B+.
export function DashboardPage(): JSX.Element {
  const q = useDashboard();
  if (q.isLoading) return <div className="p-4 text-sm text-zinc-600">Loading your dashboard…</div>;
  if (q.isError || !q.data) return <div className="p-4 text-sm text-brand-red">Couldn&rsquo;t load — refresh.</div>;

  return <DashboardBody payload={q.data} />;
}

function DashboardBody({ payload }: { payload: DashboardPayload }): JSX.Element {
  const hasAny = payload.active.length + payload.upcoming.length + payload.past.length > 0;
  if (!hasAny) return <EmptyState />;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Portal"
        title="Your dashboard"
        description="Active rentals, upcoming reservations, and order history."
      />

      <KpiStrip payload={payload} />

      <QuickAccessRail />

      <Section title="Active rentals" count={payload.active.length}>
        {payload.active.length === 0 ? (
          <Hint>No active rentals right now.</Hint>
        ) : (
          payload.active.map((o) => (
            <ActiveCard key={o.order_number} order={o} disabled={payload.delinquent} />
          ))
        )}
      </Section>

      <Section title="Upcoming reservations" count={payload.upcoming.length}>
        {payload.upcoming.length === 0 ? (
          <Hint>No upcoming reservations yet.</Hint>
        ) : (
          payload.upcoming.map((o) => <UpcomingCard key={o.order_number} order={o} />)
        )}
      </Section>

      <Section title="Past orders" count={payload.past.length}>
        {payload.past.length === 0 ? (
          <Hint>
            Past rentals show here so you can quickly reorder.{" "}
            <Link to="/" className="text-brand-red underline">
              Browse the catalog →
            </Link>
          </Hint>
        ) : (
          <ul className="divide-y divide-zinc-200 overflow-hidden rounded-lg border border-zinc-200 bg-brand-white">
            {payload.past.map((o) => (
              <li key={o.order_number} className="flex items-center gap-4 px-3 py-3 text-sm">
                <EquipmentThumb src={o.equipment_image_url} name={o.equipment_summary} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium text-brand-black">{o.equipment_summary}</p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {formatDate(o.delivery_date)} – {formatDate(o.pickup_date)} ·{" "}
                    <Link
                      to={`/portal/orders/${o.order_number}`}
                      className="font-mono text-zinc-600 underline"
                    >
                      #{o.order_number}
                    </Link>
                  </p>
                </div>
                <StatusPill status={o.status} />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function KpiStrip({ payload }: { payload: DashboardPayload }): JSX.Element {
  const activeCount = payload.active.length;
  const upcomingCount = payload.upcoming.length;
  const coiSatisfied = payload.active.every((o) => !o.coi_required || o.coi_approved_at != null);

  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="kpi-strip">
      <KpiTile
        label="Active"
        value={String(activeCount)}
        accent={activeCount > 0 ? "info" : "neutral"}
      />
      <KpiTile
        label="Upcoming"
        value={String(upcomingCount)}
        accent={upcomingCount > 0 ? "info" : "neutral"}
      />
      <KpiTile
        label="Balance"
        value={payload.delinquent ? "Overdue" : "$0"}
        accent={payload.delinquent ? "danger" : "success"}
      />
      <KpiTile
        label="Insurance"
        value={coiSatisfied ? "On file" : "Action needed"}
        accent={coiSatisfied ? "success" : "warning"}
      />
    </dl>
  );
}

type KpiAccent = "neutral" | "info" | "success" | "warning" | "danger";
const ACCENT_VALUE_CLASS: Record<KpiAccent, string> = {
  neutral: "text-zinc-700",
  info: "text-flag-navy",
  success: "text-emerald-700",
  warning: "text-amber-700",
  danger: "text-brand-red"
};

function KpiTile({
  label,
  value,
  accent
}: {
  label: string;
  value: string;
  accent: KpiAccent;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-zinc-200 bg-brand-white px-4 py-3 shadow-sm">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </dt>
      <dd className={`mt-1 text-xl font-semibold ${ACCENT_VALUE_CLASS[accent]}`}>{value}</dd>
    </div>
  );
}

function ActiveCard({
  order,
  disabled
}: {
  order: DashboardOrderCard;
  disabled: boolean;
}): JSX.Element {
  const ready = useReadyForPickup(order.order_number);
  const alreadyPressed = order.pickup_notice_at != null || ready.data?.already_notified === true;

  return (
    <article
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm"
      data-testid={`active-${order.order_number}`}
    >
      <div className="flex items-start gap-4">
        <EquipmentThumb
          src={order.equipment_image_url}
          name={order.equipment_summary}
          size="md"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-brand-black">
                <Link
                  to={`/portal/orders/${order.order_number}`}
                  className="hover:underline"
                >
                  {order.equipment_summary || "Equipment"}
                </Link>
              </h3>
              <p className="mt-0.5 font-mono text-xs text-zinc-500">
                #{order.order_number}
              </p>
            </div>
            <StatusPill status={order.status} />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <CountdownChip label="Pickup" days={order.days_remaining} />
            <span className="text-zinc-500">
              Delivered {formatDate(order.delivery_date)} · Pickup {formatDate(order.pickup_date)}
            </span>
          </div>

          <div className="mt-3">
            <LifecycleStepper status={order.status} />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={alreadyPressed || ready.isPending || disabled}
              onClick={() => ready.mutate()}
              className="min-h-[44px] rounded-md bg-brand-red px-4 py-2 text-sm font-semibold uppercase tracking-wide text-brand-white shadow-sm transition hover:bg-brand-red-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`ready-${order.order_number}`}
            >
              {alreadyPressed
                ? "Pickup requested ✓"
                : ready.isPending
                  ? "Submitting…"
                  : "Notify ready for pickup"}
            </button>
            <Link
              to={`/portal/orders/${order.order_number}#issue`}
              className="min-h-[44px] inline-flex items-center rounded-md px-3 py-2 text-sm text-brand-black hover:text-brand-red"
            >
              Report an issue
            </Link>
            {!disabled ? (
              <Link
                to={`/portal/reorder/${order.order_number}`}
                className="min-h-[44px] inline-flex items-center rounded-md px-3 py-2 text-sm text-zinc-600 hover:text-brand-black"
              >
                Reorder
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function UpcomingCard({ order }: { order: DashboardOrderCard }): JSX.Element {
  const coiOk = !order.coi_required || order.coi_approved_at != null;
  return (
    <article
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm"
      data-testid={`upcoming-${order.order_number}`}
    >
      <div className="flex items-start gap-4">
        <EquipmentThumb
          src={order.equipment_image_url}
          name={order.equipment_summary}
          size="md"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-brand-black">
                <Link
                  to={`/portal/orders/${order.order_number}`}
                  className="hover:underline"
                >
                  {order.equipment_summary || "Equipment"}
                </Link>
              </h3>
              <p className="mt-0.5 font-mono text-xs text-zinc-500">
                #{order.order_number}
              </p>
            </div>
            <StatusPill status={order.status} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-600">
            <CountdownChip label="Delivery" days={daysUntil(order.delivery_date)} />
            <span>
              Delivery {formatDate(order.delivery_date)} · COI{" "}
              <strong className={coiOk ? "text-emerald-700" : "text-amber-700"}>
                {coiOk ? "Approved" : "Pending"}
              </strong>
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function Section({
  title,
  count,
  children
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section>
      <header className="mb-3 flex items-baseline gap-3">
        <h2 className="font-serif text-lg font-bold italic tracking-tight text-brand-black md:text-xl">
          {title}
        </h2>
        <span className="text-sm font-medium text-zinc-500">({count})</span>
        <span aria-hidden="true" className="ml-1 inline-block h-0.5 w-10 bg-brand-red" />
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Hint({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm text-zinc-500">{children}</p>;
}

function EmptyState(): JSX.Element {
  return (
    <div className="space-y-6" data-testid="dashboard-empty">
      <div
        className="rounded-lg border border-dashed border-zinc-300 bg-brand-white p-10 text-center"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">Dashboard</p>
        <h2 className="mt-2 font-serif text-2xl font-bold italic tracking-tight text-brand-black md:text-3xl">
          No rentals yet
        </h2>
        <p className="mt-3 text-sm text-zinc-600">
          When you place your first reservation, it shows up here.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-brand-red px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-white shadow-lg shadow-brand-red/30 transition hover:bg-brand-red-dark"
        >
          Browse equipment
          <span aria-hidden="true">→</span>
        </Link>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
          Set up your account
        </p>
        <QuickAccessRail />
      </div>
    </div>
  );
}
